import { create } from 'zustand';
import { AuthAxios } from '@/shared/api/axiosInstance';

// ─── Types ───────────────────────────────────────────────────────────────────

interface AccessPermission {
  id?: number;
  allow_view: boolean;
  allow_add: boolean;
  allow_update: boolean;
  allow_delete: boolean;
  allow_print: boolean;
}

export interface FeatureItem {
  feature: string;
  parent_feature: string;
  feature_path: string;
  feature_authorization: string;
  children?: FeatureItem[];
}

export interface PermissionState {
  features: FeatureItem[];
  featureAccess: Record<string, AccessPermission>;
  isLoaded: boolean;
  isLoading: boolean;
}

export interface PermissionActions {
  setPermissions: (data: {
    features: FeatureItem[];
    feature_access: Record<string, AccessPermission>;
  }) => void;
  clearPermissions: () => void;
  fetchSessionInfo: () => Promise<void>;
}

export type PermissionStore = PermissionState & PermissionActions;

// ─── Store (NO persist — memory only) ────────────────────────────────────────

const usePermissionStore = create<PermissionStore>((set, get) => ({
  features: [],
  featureAccess: {},
  isLoaded: false,
  isLoading: false,

  setPermissions: (data) => {
    set({
      features: data.features ?? [],
      featureAccess: data.feature_access ?? {},
      isLoaded: true,
      isLoading: false,
    });
  },

  clearPermissions: () => {
    set({
      features: [],
      featureAccess: {},
      isLoaded: false,
      isLoading: false,
    });
  },

  fetchSessionInfo: async () => {
    // Prevent duplicate fetches
    if (get().isLoading) return;

    set({ isLoading: true });
    try {
      const res = await AuthAxios.get('/auth/session-info');
      const data = res.data?.data;
      if (data) {
        set({
          features: data.features ?? [],
          featureAccess: data.feature_access ?? {},
          isLoaded: true,
          isLoading: false,
        });
      } else {
        set({ isLoading: false });
      }
    } catch (error) {
      console.error('[usePermissionStore] fetchSessionInfo failed:', error);
      set({ isLoading: false });
    }
  },
}));

export default usePermissionStore;
